import type { ClientError } from "@acme/backend/utils/clientError";
import type { SupportedLanguage } from "@acme/i18n";
import { Spinner } from "@acme/ui/spinner";
import { QueryClientProvider, useQueryErrorResetBoundary } from "@tanstack/react-query";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { TRPCClientError } from "@trpc/client";
import { useEffect } from "react";
import { DeafultErrorPage } from "~/common/pages/deafult-error-page.js";
import { NotFound404Page } from "~/common/pages/not-found-404.js";
import { InvalidateQueriesProvider } from "~/common/providers/invalidate-queries-provider.js";
// import type { AuthContext } from "~/features/auth/_providers/auth.provider.js";
import { api, queryClient } from "~/common/utils/trpc.js";

import { routeTree } from "./routeTree.gen.js";

const DefaultErrorComponent = ({ error, info: _info, reset: _reset }: ErrorComponentProps) => {
  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  if (error instanceof TRPCClientError) {
    const clientError = error.data.clientError as ClientError;

    if (clientError.identifier === "not-found") {
      return <NotFound404Page />;
    }
  }

  return <DeafultErrorPage />;
};

function DefaultPendingComponent() {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background">
      <Spinner className="size-8 text-muted-foreground" />
    </div>
  );
}

/**
 * Router-Optionen (vgl. TanStack Router RouterOptions):
 * - defaultPreload "intent": Preload nur bei Hover/Touch, reduziert unnötige Loader-Last.
 * - defaultPendingMs/defaultPendingMinMs: Verhindert Flackern bei schnellen Navigationen.
 * - router.invalidate() in main.tsx wird nur bei echter Auth-Änderung (nicht bei Referenz-Wechsel) aufgerufen,
 *   damit PWA-Fokus (z. B. iOS) keine unnötige Invalidierung auslöst.
 */
export const createRouter = () => {
  const router = createTanStackRouter({
    Wrap: function WrapComponent({ children }: { readonly children: React.ReactNode }) {
      return (
        <QueryClientProvider client={queryClient}>
          <InvalidateQueriesProvider>{children}</InvalidateQueriesProvider>
        </QueryClientProvider>
      );
    },
    context: {
      api,
      /** Set by InnerApp; only populated after session load (AuthSessionGate ensures router mounts when isPending is false). */
      auth: undefined,
      queryClient,
      locale: "en",
    },
    defaultErrorComponent: DefaultErrorComponent,
    defaultHashScrollIntoView: true,
    defaultNotFoundComponent: NotFound404Page,
    defaultPendingComponent: DefaultPendingComponent,
    defaultPendingMinMs: 500,
    defaultPendingMs: 1000,
    defaultPreload: "intent",
    defaultViewTransition: true,
    routeTree,
    scrollRestoration: true,
  });

  return router;
};

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createRouter>;
  }
}
